import { CubeState } from '../core/cube-state.js';
import { applyAlgorithmToState, applyMoveToState } from '../core/moves.js';
import { CubeView } from '../view/cube-view.js';
import {
  DEFAULT_SCRAMBLE_LENGTH,
  MAX_SCRAMBLE_LENGTH,
  MIN_SCRAMBLE_LENGTH,
  SCRAMBLE_ORDER_OPTIONS,
  clampScrambleLength,
  generateScramble,
  generateScramble3x3,
} from './scramble-generator.js';

export function scrambleGeneratorTemplate(defaultLength = DEFAULT_SCRAMBLE_LENGTH) {
  const initialLength = clampScrambleLength(defaultLength);
  const orderOptions = SCRAMBLE_ORDER_OPTIONS.map((item) => (
    `<option value="${item.order}"${item.order === 3 ? ' selected' : ''}${item.enabled ? '' : ' disabled'}>${item.label}${item.enabled ? '' : '（暂未开放）'}</option>`
  )).join('');

  return `<section class="scramble-generator-page" aria-labelledby="scramble-generator-title">
    <div class="scramble-generator-copy">
      <button class="back-button" type="button" data-scramble-action="back-timer"><span aria-hidden="true">←</span> 返回计时器</button>
      <p class="eyebrow">计时器扩展</p>
      <h1 id="scramble-generator-title">打乱公式生成</h1>
      <p class="hero-description">设置本次公式长度，生成使用标准面转记号的 3×3 随机打乱步骤。</p>

      <div class="scramble-generator-form" aria-label="打乱公式参数">
        <label>
          <span>阶数</span>
          <select data-scramble-order>${orderOptions}</select>
        </label>
        <label>
          <span>打乱次数</span>
          <input type="number" inputmode="numeric" min="${MIN_SCRAMBLE_LENGTH}" max="${MAX_SCRAMBLE_LENGTH}" step="1" value="${initialLength}" data-scramble-length>
          <small>允许 ${MIN_SCRAMBLE_LENGTH}～${MAX_SCRAMBLE_LENGTH} 步，超出后自动调整。</small>
        </label>
        <button class="primary-button" type="button" data-scramble-action="generate">立即生成 <span aria-hidden="true">→</span></button>
      </div>
      <p class="scramble-generator-note">右侧动画仅用于效果展示，不会作为最终打乱公式。</p>
    </div>

    <div class="scramble-generator-stage">
      <div class="cube-canvas scramble-generator-canvas" data-scramble-preview-canvas></div>
      <p class="gesture-hint"><span aria-hidden="true">↻</span> 拖动旋转 · 展示动画持续播放</p>
    </div>
  </section>`;
}

export class ScrambleGeneratorPage {
  /**
   * @param {any} root
   * @param {{ announce?: (message: string) => void, defaultLength?: number, onGenerate?: (scramble: any) => void }} [options]
   */
  constructor(root, options = {}) {
    const {
      announce = (_message) => {},
      defaultLength = DEFAULT_SCRAMBLE_LENGTH,
      onGenerate = (_scramble) => {},
    } = options;
    this.root = root;
    this.announce = announce;
    this.onGenerate = onGenerate;
    this.disposed = false;
    this.root.innerHTML = scrambleGeneratorTemplate(defaultLength);
    this.view = new CubeView(this.root.querySelector('[data-scramble-preview-canvas]'));
    this.previewStickers = CubeState.solved().stickers;
    this.view.setStickers(this.previewStickers);
    this.handleClick = (event) => this.#handleClick(event);
    this.handleChange = (event) => this.#handleChange(event);
    this.root.addEventListener('click', this.handleClick);
    this.root.addEventListener('input', this.handleChange);
    this.root.addEventListener('change', this.handleChange);
    this.root.addEventListener('focusout', this.handleChange);
    void this.#startPreview();
  }

  dispose() {
    this.disposed = true;
    this.root.removeEventListener('click', this.handleClick);
    this.root.removeEventListener('input', this.handleChange);
    this.root.removeEventListener('change', this.handleChange);
    this.root.removeEventListener('focusout', this.handleChange);
    this.view.dispose();
  }

  #handleClick(event) {
    const action = event.target.closest('[data-scramble-action]')?.dataset.scrambleAction;
    if (action === 'back-timer') window.location.hash = '#/timer';
    else if (action === 'generate') {
      const input = this.root.querySelector('[data-scramble-length]');
      const length = clampScrambleLength(input.value);
      input.value = String(length);
      try {
        this.onGenerate(generateScramble(Number(this.root.querySelector('[data-scramble-order]').value), length));
      } catch (cause) {
        this.announce(cause?.message ?? String(cause));
      }
    }
  }

  #handleChange(event) {
    if (!event.target.matches('[data-scramble-length]')) return;
    event.target.value = String(clampScrambleLength(event.target.value));
  }

  async #startPreview() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      this.previewStickers = applyAlgorithmToState(this.previewStickers, generateScramble3x3(15).moves);
      this.view.setStickers(this.previewStickers);
      return;
    }

    try {
      while (!this.disposed) {
        const preview = generateScramble3x3(18);
        for (const move of preview.moves) {
          if (this.disposed) return;
          const nextStickers = applyMoveToState(this.previewStickers, move);
          await this.view.animateMove(move, { duration: 560, finalStickers: nextStickers });
          this.previewStickers = nextStickers;
        }
      }
    } catch (cause) {
      if (!this.disposed) this.announce(`展示动画已暂停：${cause?.message ?? String(cause)}`);
    }
  }
}
